// shell/components/entity-fields/view-renderers/html-renderer.ts

import { createElement } from '../../../utils/dom';
import type { HtmlViewField } from '../../entity-view/types';

/**
 * Renders HTML content with optional sanitization.
 */
export function renderHtmlView<T>(
    field: HtmlViewField<T>,
    value: unknown,
    _model: T
): HTMLElement {
    const container = createElement('div', {
        className: 'ev-field-view ev-field-view--html',
    });

    if (value === null || value === undefined || value === '') {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    const htmlContent = String(value);
    const shouldSanitize = field.sanitize !== false;

    if (shouldSanitize) {
        container.innerHTML = sanitizeHtml(htmlContent);
    } else {
        container.innerHTML = htmlContent;
    }

    return container;
}

/**
 * Basic HTML sanitization.
 * Removes script tags, event handlers, and dangerous attributes.
 */
function sanitizeHtml(html: string): string {
    // Create a temporary container
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Remove dangerous elements
    const dangerousTags = ['script', 'iframe', 'object', 'embed', 'form'];
    for (const tag of dangerousTags) {
        const elements = temp.getElementsByTagName(tag);
        while (elements.length > 0) {
            elements[0].remove();
        }
    }

    // Remove event handlers and dangerous attributes
    const allElements = temp.getElementsByTagName('*');
    for (let i = 0; i < allElements.length; i++) {
        const el = allElements[i];
        const attrs = Array.from(el.attributes);

        for (const attr of attrs) {
            const name = attr.name.toLowerCase();
            // Remove event handlers (onclick, onload, etc.)
            if (name.startsWith('on')) {
                el.removeAttribute(attr.name);
            }
            // Remove javascript: URLs
            if (['href', 'src', 'action'].includes(name)) {
                const value = attr.value.toLowerCase().trim();
                if (value.startsWith('javascript:') || value.startsWith('data:')) {
                    el.removeAttribute(attr.name);
                }
            }
        }
    }

    return temp.innerHTML;
}
